import React, { useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  Grid,
  GridColumn,
  GridRow,
  Gutter,
  Layout,
  Suspense,
  useApi,
} from '@axiom/ui';
import type { OpportunityCandidate } from '@axiom/validation';

import { AccountBenchApi } from '../../api/account-bench-api';
import { MlRecommendationsApi } from '../../api/ml-recommendations-api';
import { OpportunityLegacyApi } from '../../api/opportunity-legacy-api';
import { CandidateUtil } from '../../utils/candidate-util';
import { PositionUtil } from '../../utils/position-util';
import { OpportunitiesContextMenu } from '../OpportunitiesContextMenu/OpportunitiesContextMenu';
import { TalentProfile } from '../TalentProfile/TalentProfile';

import { OpportunityTalentClientBench } from './OpportunityTalentClientBench';
import { OpportunityTalentParentPosition } from './OpportunityTalentParentPosition';
import { OpportunityTalentParentRemovedTalent } from './OpportunityTalentParentRemovedTalent';
import { OpportunityTalentEmptyProfile } from './OpportunityTalentEmptyProfile';
import { OpportunityTalentCandidate } from './OpportunityTalentCandidate';

export const OpportunityTalent = () => {
  const navigate = useNavigate();
  const { id: opportunityId, view, viewId: candidateId } = useParams();

  if (!opportunityId) {
    throw new Error('No opportunity id found');
  }

  const [
    { data: opportunity },
    { data: candidates },
    { data: positions },
    { data: mlRecommendations },
    { data: benchResponse },
  ] = useApi(
    OpportunityLegacyApi.readOpportunity(opportunityId),
    OpportunityLegacyApi.readOpportunityCandidates(opportunityId),
    OpportunityLegacyApi.readOpportunityPositions(opportunityId),
    MlRecommendationsApi.readRecommendations(opportunityId),
    AccountBenchApi.readOpportunityBench({ opportunityId })
  );

  /**
   * We need to always pull fresh data when we load this component
   */
  useEffect(() => {
    void OpportunityLegacyApi.refreshOpportunity(opportunityId);
    void OpportunityLegacyApi.refreshOpportunityCandidates(opportunityId);
    void OpportunityLegacyApi.refreshOpportunityPositions(opportunityId);
  }, []);

  const hasBeenAiMatched = mlRecommendations.length > 0;
  const mlRecs = useMemo(() => {
    const candidateIds = new Set(candidates.map(c => c.candidateId));
    return mlRecommendations.filter(rec => !candidateIds.has(rec.candidateId));
  }, [mlRecommendations, candidates]);

  const sortedPositions = useMemo(
    () => PositionUtil.sortPositions(positions),
    [positions]
  );

  const talentPool = useMemo(
    () => candidates.filter(c => !c.positionId),
    [candidates]
  );

  const activePositionCandidates = useMemo(
    () =>
      sortedPositions.map(position => ({
        id: position.id,
        candidates: position.candidates,
        activeCandidates: CandidateUtil.filterRemovedCandidates(
          position.candidates
        ),
      })),
    [sortedPositions]
  );

  const activeTalentPool = useMemo(
    () => CandidateUtil.filterRemovedCandidates(talentPool),
    [talentPool]
  );

  // Auto select Talent from positions, if not available select from talent pool
  useEffect(() => {
    if (view !== 'talent') return;
    if (candidateId) return;

    const firstAvailableCandidateId =
      activePositionCandidates.flatMap(p => p.activeCandidates)[0]
        ?.candidateId ??
      mlRecs[0]?.candidateId ??
      activeTalentPool[0]?.candidateId ??
      null;

    if (firstAvailableCandidateId) {
      void navigate(
        `/opportunity-detail/${opportunityId}/talent/${firstAvailableCandidateId}`,
        {
          replace: true,
        }
      );
    }
  }, [
    view,
    candidateId,
    activePositionCandidates,
    activeTalentPool,
    mlRecs,
    navigate,
    opportunityId,
  ]);

  const candidateProfileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    candidateProfileRef.current?.scrollIntoView({ block: 'start' });
  }, [candidateId]);

  const isBenchCandidate =
    !!candidateId &&
    !candidates.some(c => c.candidateId === candidateId) &&
    !mlRecs.some(r => r.candidateId === candidateId) &&
    benchResponse.candidates.some(c => c.candidateId === candidateId);

  const showCandidateProfile =
    candidateId &&
    (candidates.some(c => c.candidateId === candidateId) ||
      mlRecs.some(r => r.candidateId === candidateId) ||
      isBenchCandidate);

  return (
    <Grid columns={12} name="OPPORTUNITY_TALENT_GRID">
      <GridRow>
        <GridColumn width={4}>
          {opportunity.isSynced ? (
            <Gutter bottom="16px">
              <OpportunitiesContextMenu
                opportunityPositions={positions}
                opportunity={opportunity}
                showAnchorButton
              />
            </Gutter>
          ) : null}
          <Suspense>
            <Layout direction="vertical" verticalGutter="16px">
              {sortedPositions.map((position, index) => (
                <OpportunityTalentParentPosition
                  key={position.id}
                  name={position.id}
                  positionId={position.id}
                  positions={positions}
                  title={PositionUtil.getNameLabel({
                    prefix: 'Position',
                    position,
                    ordinal: index + 1,
                  })}
                  opportunity={opportunity}
                  candidates={
                    CandidateUtil.filterRemovedCandidates(
                      position.candidates
                    ) as OpportunityCandidate[]
                  }
                  isFilled={PositionUtil.isPositionFulfilled(
                    position.candidates
                  )}
                />
              ))}

              <OpportunityTalentParentPosition
                name="talent-pool"
                title="Talent Pool"
                opportunity={opportunity}
                candidates={
                  CandidateUtil.filterRemovedCandidates(
                    talentPool
                  ) as OpportunityCandidate[]
                }
                isTalentPool
                hasBeenAiMatched={hasBeenAiMatched}
                mlRecommendations={CandidateUtil.filterRemovedMLRecommendations(
                  mlRecs
                )}
              />

              {benchResponse.candidates.length > 0 ? (
                <OpportunityTalentClientBench
                  opportunity={opportunity}
                  benchCandidates={benchResponse.candidates}
                />
              ) : null}

              <OpportunityTalentParentRemovedTalent
                opportunity={opportunity}
                candidates={
                  CandidateUtil.getRemovedCandidates(
                    candidates
                  ) as OpportunityCandidate[]
                }
                mlRecommendations={CandidateUtil.getRemovedMLRecommendations(
                  mlRecs
                )}
              />
            </Layout>
          </Suspense>
        </GridColumn>
        <GridColumn width={8}>
          <div ref={candidateProfileRef}>
            {showCandidateProfile ? (
              <OpportunityTalentCandidate>
                <TalentProfile
                  candidateId={candidateId}
                  opportunityId={opportunityId}
                  isBenchCandidate={isBenchCandidate}
                  expanded
                  isDetail
                  hideTitleBar
                  hideWarnings
                />
              </OpportunityTalentCandidate>
            ) : (
              <OpportunityTalentEmptyProfile />
            )}
          </div>
        </GridColumn>
      </GridRow>
    </Grid>
  );
};
